import { defineStore } from 'pinia'
import { ref, Ref } from 'vue'
import type { AxiosError, AxiosResponse } from 'axios'
import { instance } from '@cognitivecredit/ui-utils'
import { getApiDomain } from '@/config'

export interface CreditCurveDiagnostics {
  rmse: number
  mae: number
  max_abs_error: number
  r2: number
  weighted_rmse: number
  n_obs: number
  n_bonds: number
  rmse_decile: number
}

export interface CreditCurveInstrument {
  identifier: string
  margin: number | null
  x_value: number | null
  y_value: number | null
  weight: number | null
  ns_residual: number | null
  ns_rolling_avg_residual_3m: number | null
  trace_trade_volume_30d: number | null
  on_the_run: boolean | null
  is_most_liquid: boolean | null
}

export interface CreditCurve {
  company_id: string
  date: string
  currency: string
  rating: string
  x_column_name: string
  y_column_name: string
  beta0: number
  beta1: number
  beta2: number
  tau: number
  implied_levels: {
    yr3: number | null
    yr5: number | null
    yr10: number | null
  }
  steepness: {
    ten_minus_three: number | null
    ten_minus_five: number | null
    five_minus_two: number | null
  }
  diagnostics: CreditCurveDiagnostics | null
  instruments: CreditCurveInstrument[]
}

export interface FailedCurve {
  company_id: string
  date: string
  currency: string
  rating: string
  reason: string
  n_bonds: number
}

export interface CreditCurvesResponse {
  curves: CreditCurve[]
  failed_curves: FailedCurve[]
}

export interface BondCompany {
  company_id: string
  name: string
  ticker: string
  default_currency: string
}

export const useCreditCurvesStore = defineStore('creditCurves', () => {
  const curves: Ref<CreditCurve[]> = ref([])
  const failedCurves: Ref<FailedCurve[]> = ref([])
  const loading: Ref<boolean> = ref(false)
  const error: Ref<string | null> = ref(null)
  const notFound: Ref<boolean> = ref(false)
  const bondCompanies: Ref<BondCompany[]> = ref([])
  const bondCompaniesLoaded: Ref<boolean> = ref(false)
  const bondCompaniesRequest: Ref<Promise<AxiosResponse<BondCompany[], any>> | null> = ref(null)
  const bondCompaniesError: Ref<string | null> = ref(null)
  // Guards against a stale request's response mutating state after a newer request has started
  let requestId = 0

  const fetchCreditCurves = async (args: { companyIds?: string[]; dates?: string[]; currency?: string } = {}) => {
    if (!args.companyIds?.length) {
      throw new Error('At least one company ID is required')
    }

    const currentRequestId = ++requestId
    loading.value = true
    error.value = null
    notFound.value = false
    try {
      const req = await instance({ prefix: 'market-data', apiDomain: getApiDomain('market-data') })
      const params: Record<string, string> = {
        companyIds: args.companyIds.join(','),
      }
      if (args.dates?.length) {
        params.dates = args.dates.join(',')
      }
      if (args.currency) {
        params.currency = args.currency
      }
      const { data } = await req.get<CreditCurvesResponse>('credit-curves', { params })
      if (currentRequestId !== requestId) {
        return
      }
      curves.value = data.curves
      failedCurves.value = data.failed_curves
    } catch (err) {
      if (currentRequestId !== requestId) {
        return
      }
      const isNotFound = (err as Error).name === 'AxiosError' && (err as AxiosError).response?.status === 404
      if (isNotFound) {
        curves.value = []
        failedCurves.value = []
        notFound.value = true
      } else {
        console.error('[ERROR] Fetching credit curves', err)
        error.value = 'Failed to load credit curves'
      }
    } finally {
      if (currentRequestId === requestId) {
        loading.value = false
      }
    }
  }

  const loadBondCompanies = () => {
    // loadBondCompanies may be called multiple times in quick succession, so we want to avoid
    // making multiple API calls. The promise is assigned synchronously (before any internal
    // await can suspend) so concurrent callers all await the same in-flight request.
    if (!bondCompaniesRequest.value) {
      bondCompaniesError.value = null
      bondCompaniesRequest.value = (async () => {
        const req = await instance({ prefix: 'market-data', apiDomain: getApiDomain('market-data') })
        return req.get<BondCompany[]>('bond-companies') as Promise<AxiosResponse<BondCompany[], any>>
      })()
    }
    const request = bondCompaniesRequest.value
      .then((res) => {
        bondCompanies.value = res.data
        bondCompaniesLoaded.value = true
      })
      .catch((err) => {
        console.error('[ERROR] Loading bond companies', err)
        bondCompaniesError.value = 'Failed to load bond companies'
      })
      .finally(() => {
        bondCompaniesRequest.value = null
      })
    return request
  }

  return {
    curves,
    failedCurves,
    loading,
    error,
    notFound,
    fetchCreditCurves,
    bondCompanies,
    bondCompaniesLoaded,
    bondCompaniesError,
    loadBondCompanies,
  }
})
